import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Phone, Award, Users, Wrench, Heart, Clock } from "lucide-react";

const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_9ea2b88f-5c7d-4521-a088-49d68618abb6/artifacts/kj811n6n_Teamfoto_bearbeitet.jpg";
const PHONE_NUMBER = "tel:+493916357115";

const stats = [
  { value: "35+", label: "Jahre Erfahrung", icon: Award },
  { value: "1988", label: "Gegründet", icon: Heart },
  { value: "10+", label: "Fachkräfte", icon: Users },
  { value: "1000+", label: "Zufriedene Kunden", icon: Wrench },
];

const values = [
  {
    title: "Qualität",
    description: "Höchste Standards in jedem Arbeitsschritt.",
    icon: Award
  },
  {
    title: "Erfahrung",
    description: "Über 35 Jahre Expertise im Handwerk.",
    icon: Clock
  },
  {
    title: "Technik",
    description: "Modernste Ausstattung und digitale Messung.",
    icon: Wrench
  },
  {
    title: "Familie",
    description: "Persönlicher Service und Vertrauen.",
    icon: Heart
  }
];

export const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="ueber-uns"
      ref={sectionRef}
      className="py-20 lg:py-28 section-light"
    >
      <div className="container mx-auto px-4 lg:px-8">
        {/* Main Content */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-700`}>
          {/* Text Content */}
          <div>
            <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
              Über uns
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Das Lack Zentrum Wilke
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Ob Lackschäden oder Karosserieprobleme – kompetente und erfahrene Hände sind genau das, 
              was Sie jetzt brauchen. Bei uns sind Sie an der richtigen Stelle.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Seit 1988 kümmern wir uns um Ihr Auto – mit geschulten Augen und ruhigen Händen sorgen 
              wir dafür, dass Kratzer und Dellen schnell vergessen werden. Unser Team aus erfahrenen 
              Fahrzeuglackierern und Karosseriebauern steht für Qualität, Zuverlässigkeit und 
              kundenorientierten Service.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Mit modernster Ausstattung, regelmäßigen Schulungen und einem unerschütterlichen 
              Qualitätsanspruch garantieren wir erstklassige Ergebnisse. Dabei vergessen wir nie, 
              dass hinter jedem Fahrzeug ein Mensch steht, dem wir mit Respekt und Engagement begegnen.
            </p>
            <Button variant="primary" size="lg" asChild className="gap-2">
              <a href={PHONE_NUMBER}>
                <Phone className="h-4 w-4" />
                Lernen Sie uns persönlich kennen
              </a>
            </Button>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src={TEAM_IMAGE}
                alt="Das Team vom Lack Zentrum Wilke"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Decorative Element */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-2xl -z-10" />
          </div>
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20 ${isVisible ? 'stagger-children' : ''}`}>
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-6 border border-border/50 text-center hover:border-primary/30 transition-colors duration-300"
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <stat.icon className="h-6 w-6 text-primary" />
              </div>
              <div className="font-display text-3xl sm:text-4xl font-bold text-primary mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-muted-foreground">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h3 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-4">
            Unsere Werte
          </h3>
          <p className="text-muted-foreground">
            Das macht uns aus und dafür stehen wir jeden Tag.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {values.map((value, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-5 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <value.icon className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
              </div>
              <h4 className="font-display text-lg font-semibold text-card-foreground mb-2">
                {value.title}
              </h4>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
